/**
 * The deploy wizard's publish payload, built in one pure function so it can be tested against the
 * REAL backend model instead of only being exercised by clicking through the UI.
 *
 * The contract it has to honour (api/publishing.py, BulkDeployRequest + the bulk handler):
 *
 *  - `inline_config`, when present, is what is applied; `template_id` travels beside it to record
 *    which template the Adjust started from, as `intune_template_id` does beside
 *    `intune_inline_config`. That is how a deploy that keeps the same adjustment is recognised as
 *    keeping the template, so the edits made on the deployment page are kept.
 *  - `inline_config` is baked onto the install command as `-DeploymentConfig b64:…` via
 *    `template_service.deployment_config_arg`, which runs the config through `render()`. So an
 *    override has to be a config the v2 validator accepts, not an arbitrary object.
 *  - a delivery template's assignments REPLACE intent/target/targets (publishing.py:1107), so the
 *    template path sends no targets. The AD HOC path is the opposite: it is the only thing saying
 *    who gets the app, so it must send at least one.
 *
 * ⚠️ The empty-targets trap. When no template plan resolves, the handler assigns
 * `request.targets or [{"type": request.target, "include": True}]` (publishing.py:1166). `[]` is
 * FALSY in Python, so "I chose no targets" does not mean "assign nobody" — it collapses to the
 * model defaults, intent=required target=allDevices, i.e. a silent push to every managed device.
 * `buildDeployPayload` therefore never emits an ad-hoc payload with an empty target list; the
 * wizard blocks that case before it gets here, and a test pins the behaviour.
 */
import type { DeployApp } from '../components/DeployWizard'
import type { DeploymentTarget } from '../services/api'

export type DeployIntent = 'required' | 'available' | 'uninstall'

export interface DeployTargetRow {
  type: 'group' | 'allDevices' | 'allUsers'
  id?: string
  name?: string
  include: boolean
}

/**
 * How this rollout decides who gets the app.
 *
 * `template` is the mock's model: a saved delivery template states the audience, the Intune
 * naming and the update policy. `adhoc` is the old deploy form — pick an intent and some targets,
 * no template involved — kept because a tenant with no templates yet must still be able to
 * deploy, and because not every one-off rollout is worth a saved template.
 */
export type Delivery =
  | { mode: 'template'; depId: string }
  | { mode: 'adhoc'; intent: DeployIntent; targets: DeployTargetRow[] }

export interface DeployPayloadInput {
  apps: DeployApp[]
  /** The chosen UX template, or null for the package's own behaviour / an adjusted rollout. */
  uxId: string | null
  /** This rollout's own UX settings; null = use the template as-is. */
  uxOverride: any | null
  delivery: Delivery
  /** F-7: adjusted delivery settings for THIS publish. Never written back to the template. */
  intuneInlineConfig?: any
  /** app id -> chosen install context. Absent or '' means "leave it to the package", which is
   *  what the backend derives from install_scope. */
  installContexts?: Record<string, string>
  /** Drop the edits made on the deployment page, so the templates' values apply. */
  discardEdits?: boolean
  /** Which deployment of the package this creates or updates. Omitted, the backend updates the
   *  first deployment or publishes one, and refuses a package with several deployments. */
  deployment?: DeploymentTarget
}

export interface DeployPayload {
  apps: {
    app_id: string
    kind: 'private' | 'public'
    install_context?: 'system' | 'user'
    deployment?: DeploymentTarget
  }[]
  template_id?: string
  inline_config?: any
  intune_template_id?: string
  intune_inline_config?: any
  intent?: DeployIntent
  target?: 'allDevices' | 'allUsers'
  targets: DeployTargetRow[]
  discard_edits?: boolean
}

export function buildDeployPayload({
  apps,
  uxId,
  uxOverride,
  delivery,
  intuneInlineConfig,
  installContexts,
  discardEdits,
  deployment,
}: DeployPayloadInput): DeployPayload {
  const base = {
    apps: apps.map((a): DeployPayload['apps'][number] => {
      // Only sent when the admin actually chose one. An empty string would be a value the
      // backend has to interpret, where absence already means "derive it from the package".
      const ctx = installContexts?.[a.id]
      const item: DeployPayload['apps'][number] =
        ctx === 'system' || ctx === 'user'
          ? { app_id: a.id, kind: a.kind, install_context: ctx }
          : { app_id: a.id, kind: a.kind }
      if (deployment) {
        // The name is trimmed here so the uniqueness check and the suffix read the same text.
        item.deployment =
          deployment.action === 'new'
            ? { action: 'new', uid: null, name: deployment.name?.trim() || null }
            : { action: 'update', uid: deployment.uid, name: null }
      }
      return item
    }),
    // An adjusted rollout sends its OWN config, which the backend applies instead of the
    // template's: the override is a COPY (spec §7a), so editing the template afterwards does not
    // rewrite what this app went out with. The id says which template the copy started from.
    template_id: uxId ?? undefined,
    inline_config: uxOverride ?? undefined,
    // Only sent when chosen; absence keeps the edits the kept templates govern.
    ...(discardEdits ? { discard_edits: true as const } : {}),
  }

  if (delivery.mode === 'template') {
    return {
      ...base,
      // Absent for a deployment published from its own settings (its template was deleted).
      intune_template_id: delivery.depId || undefined,
      // Sent ALONGSIDE the id, not instead of it: the id records which template the settings came
      // from, and the backend prefers the inline config when both arrive. That keeps the audit
      // entry able to say "adjusted from Standard" rather than just "adjusted".
      intune_inline_config: intuneInlineConfig ?? undefined,
      // The template owns the audience; sending targets too would state a population it does not
      // describe, and the handler would have two competing answers.
      targets: [],
    }
  }

  return {
    ...base,
    intent: delivery.intent,
    // Legacy single-target fallback, kept in step with `targets` the way the old deploy form sent
    // it: only claim allUsers when that is the ONLY broad target chosen.
    target:
      delivery.targets.some((t) => t.type === 'allUsers') &&
      !delivery.targets.some((t) => t.type === 'allDevices')
        ? 'allUsers'
        : 'allDevices',
    targets: delivery.targets,
  }
}
